import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import TestimonialsSection from "@/components/TestimonialsSection";
import { ArrowRight, RotateCcw } from "lucide-react";
import { readResume, clearResume } from "@/lib/api";

export default function Landing() {
  const [resume, setResume] = useState(null);
  useEffect(() => { setResume(readResume()); }, []);

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950">
      <Header />

      {resume?.id && (
        <div data-testid="resume-banner" className="border-b border-teal-200 dark:border-teal-900/60 bg-teal-50/70 dark:bg-teal-900/20">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-3 flex items-center justify-between gap-3 flex-wrap">
            <p className="text-sm text-teal-800 dark:text-teal-200">
              <RotateCcw className="inline h-4 w-4 mr-1.5 -mt-0.5" />
              You have an assessment in progress. Pick up where you left off.
            </p>
            <div className="flex items-center gap-2">
              <Link
                to={resume.status === "completed" ? `/summary/${resume.id}` : `/assessment/${resume.id}`}
                data-testid="resume-btn"
                className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-teal-600 text-white text-sm font-medium hover:bg-teal-500 transition-colors"
              >
                Resume <ArrowRight className="h-4 w-4" />
              </Link>
              <button
                onClick={() => { clearResume(); setResume(null); }}
                data-testid="discard-resume-btn"
                className="inline-flex h-9 px-3 rounded-md border border-teal-200 dark:border-teal-800 bg-white dark:bg-slate-900 text-teal-700 dark:text-teal-300 text-xs hover:bg-teal-50 dark:hover:bg-teal-900/30 transition-colors"
              >
                Discard
              </button>
            </div>
          </div>
        </div>
      )}

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 hero-grid pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <h1
              data-testid="hero-headline"
              className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900 dark:text-slate-50 leading-[1.15]"
            >
              Identify operational dependencies, continuity gaps, and recovery priorities
            </h1>

            <p
              data-testid="hero-subheadline"
              className="mt-6 text-lg text-slate-600 dark:text-slate-300 leading-relaxed max-w-2xl"
            >
              — before they become business interruptions.
            </p>

            <div className="mt-9 flex flex-wrap items-center gap-3">
              <Link
                to="/mode"
                data-testid="hero-start-btn"
                className="group inline-flex items-center gap-2 h-11 px-5 rounded-md bg-slate-900 dark:bg-teal-600 text-white text-sm font-medium hover:bg-slate-800 dark:hover:bg-teal-500 transition-colors"
              >
                Start Assessment
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
              <a
                href="#how"
                data-testid="hero-learn-btn"
                className="inline-flex items-center h-11 px-5 rounded-md border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 text-sm font-medium hover:border-slate-300 dark:hover:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-900 transition-colors"
              >
                Learn more
              </a>
            </div>

            <Link
              to="/advisors"
              data-testid="hero-advisor-link"
              className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-teal-700 dark:text-teal-400 hover:text-teal-800 dark:hover:text-teal-300 transition-colors"
            >
              IT advisors &amp; fractional CIOs — deliver this to your clients
              <ArrowRight className="h-4 w-4" />
            </Link>

            <ul data-testid="hero-trust" className="mt-10 grid grid-cols-2 sm:grid-cols-3 gap-4 max-w-3xl">
              {[
                "Operationally focused",
                "Executive-ready output",
                "AI-assisted assessment",
              ].map((t) => (
                <li key={t} className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed border-l border-slate-200 dark:border-slate-700 pl-3">
                  {t}
                </li>
              ))}
            </ul>
          </div>

          {/* Live insights preview — auto-advancing carousel (Iteration 21) */}
          <div className="lg:col-span-5 lg:pl-6">
            <InsightsCarousel />
          </div>
        </div>
      </section>

      {/* TEAM IMAGE BAND — between hero and How it works */}
      <section className="border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <p className="text-[11px] uppercase tracking-[0.18em] text-teal-700 dark:text-teal-400">
              Built for teams
            </p>
            <h2
              data-testid="team-band-headline"
              className="mt-3 font-display text-2xl sm:text-3xl font-semibold text-slate-900 dark:text-slate-50 tracking-tight leading-[1.2]"
            >
              For teams that want to act, not audit.
            </h2>
            <p className="mt-4 text-slate-600 dark:text-slate-300 leading-relaxed">
              Bring your operators, your number two, and the colleague closest to the customer.
              Work through it together, capture what you already know, and leave with a plan
              your team can actually run.
            </p>
          </div>
          <div className="lg:col-span-7 order-1 lg:order-2">
            <div className="relative rounded-xl overflow-hidden border border-slate-200 dark:border-slate-800 shadow-sm">
              <img
                data-testid="team-band-image"
                src="/team-planning.jpg"
                alt="A diverse team of four colleagues collaborating around laptops and tablets, working through an operational resilience plan together."
                loading="lazy"
                decoding="async"
                width="1376"
                height="768"
                className="w-full h-auto block"
              />
            </div>
          </div>
        </div>
      </section>

      {/* HOW */}
      <section id="how" className="border-t border-slate-200 dark:border-slate-800 bg-teal-700 dark:bg-slate-950/60">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="max-w-2xl">
            <p className="text-[11px] uppercase tracking-[0.18em] text-teal-200 dark:text-teal-400">How it works</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-semibold text-white dark:text-slate-50 tracking-tight">
              A structured operational assistant.
            </h2>
            <p className="mt-4 text-teal-50 dark:text-slate-300 leading-relaxed">
              Choose your operating mode, work through a focused set of operational questions, and watch
              dependencies and continuity gaps become visible. End with a consulting-grade summary you
              can act on.
            </p>
          </div>

          <ol className="mt-12 grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { n: "01", t: "Select your mode", d: "Solopreneur, SME, or Corporate Department." },
              { n: "02", t: "Answer guided questions", d: "Cards appear one at a time. Suggestions and free text." },
              { n: "03", t: "Watch insights surface", d: "Single points of failure, blind spots, and risks." },
              { n: "04", t: "Export your assessment", d: "Executive-ready PDF — share with advisors." },
            ].map((s) => (
              <li key={s.n} className="rounded-lg border border-teal-600/50 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 shadow-md hover:shadow-xl hover:-translate-y-0.5 transition-all">
                <span className="text-xs font-medium text-teal-700 dark:text-teal-400">{s.n}</span>
                <h4 className="mt-2 font-display text-base font-semibold text-slate-900 dark:text-slate-50">{s.t}</h4>
                <p className="mt-1.5 text-sm text-slate-600 dark:text-slate-300 leading-relaxed">{s.d}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* WHAT IT DOES */}
      <section id="what" className="border-t border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <p className="text-[11px] uppercase tracking-[0.18em] text-teal-700 dark:text-teal-400">What it does</p>
            <h2 className="mt-3 font-display text-xl sm:text-2xl lg:text-3xl font-semibold text-slate-900 dark:text-slate-50 tracking-tight leading-[1.2]">
              Identify operational dependencies, continuity gaps, and recovery priorities
            </h2>
            <p className="mt-4 text-slate-600 dark:text-slate-300 leading-relaxed">
              — before they become business interruptions.
            </p>
            <p className="mt-3 text-slate-600 dark:text-slate-300 leading-relaxed">
              Designed to support structured operational thinking — not to replace experienced
              continuity advisors or formal compliance programs.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-2 text-xs">
              {["Operationally focused", "Executive-ready output", "AI-assisted assessment"].map((t) => (
                <span key={t} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200">
                  {t}
                </span>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <blockquote className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 shadow-sm">
              <p className="font-display text-lg text-slate-900 dark:text-slate-50 leading-snug">
                “We finally saw the operational risks we were overlooking.”
              </p>
              <p className="mt-4 text-xs uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Testimonial</p>
            </blockquote>
          </div>
        </div>
      </section>

      <TestimonialsSection />

      {/* CTA — operational rewrite */}
      <section className="border-t border-slate-200 dark:border-slate-800 bg-slate-900 dark:bg-slate-950 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <h3 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">
              Start identifying your operational blind spots.
            </h3>
            <p className="mt-2 text-slate-300 text-sm">Guided assessment. Typically 8–12 minutes.</p>
          </div>
          <Link
            to="/mode"
            data-testid="cta-start-btn"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-md bg-teal-600 hover:bg-teal-500 transition-colors text-white text-sm font-medium"
          >
            Start Assessment <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}

/**
 * Auto-advancing carousel for the 3 Live Operational Insights on the hero.
 *
 * Design notes (Iteration 21 — addresses "let it be more dynamic, take up less
 * space, mobile-friendly"):
 *   - Single card visible at a time → cuts the hero column height by ~2/3 on
 *     mobile, where this is the SECOND thing the user sees but should not be
 *     a long scroll.
 *   - Auto-advances every 4.5s with a thin top progress bar so the timing
 *     feels deliberate (not random).
 *   - Pauses on hover/focus + when the tab is hidden (no animation in the
 *     background, no battery drain).
 *   - Manual dots + keyboard arrows let the user steer.
 *   - prefers-reduced-motion → freezes on the first slide.
 *   - Severity ribbon colour (red/amber/teal) is preserved from the static
 *     version so the visual continuity carries over.
 */
const INSIGHTS = [
  {
    type: "Single Point of Failure",
    title: "Single warehouse dependency detected",
    body: "No alternate site identified for fulfilment continuity.",
    sev: "high",
  },
  {
    type: "Potential Oversight",
    title: "Possible backup oversight",
    body: "Operational spreadsheets mentioned without backup arrangement.",
    sev: "medium",
  },
  {
    type: "Dependency Observation",
    title: "Banking access appears operationally critical",
    body: "Plan for alternate signatories and backup payment routes.",
    sev: "low",
  },
];
const SEV_BAR = { high: "bg-red-500", medium: "bg-amber-500", low: "bg-teal-600" };
const SLIDE_MS = 4500;

function InsightsCarousel() {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  // Honour the user's OS-level "reduce motion" preference — freezes the
  // carousel on the first slide if they have it enabled.
  const reduceMotion = typeof window !== "undefined"
    && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

  // Auto-advance + pause when tab is hidden.
  useEffect(() => {
    if (reduceMotion || paused) return undefined;
    const onVisibility = () => setPaused(document.hidden);
    document.addEventListener("visibilitychange", onVisibility);
    const id = setInterval(() => setIdx((p) => (p + 1) % INSIGHTS.length), SLIDE_MS);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [paused, reduceMotion]);

  // Keyboard arrow navigation.
  const onKey = (e) => {
    if (e.key === "ArrowRight") setIdx((p) => (p + 1) % INSIGHTS.length);
    if (e.key === "ArrowLeft") setIdx((p) => (p - 1 + INSIGHTS.length) % INSIGHTS.length);
  };

  const current = INSIGHTS[idx];

  return (
    <div
      data-testid="insights-carousel"
      role="region"
      aria-roledescription="carousel"
      aria-label="Live Operational Insights preview"
      tabIndex={0}
      onKeyDown={onKey}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm overflow-hidden focus:outline-none focus:ring-2 focus:ring-teal-500/40"
    >
      {/* Thin progress bar — drains over SLIDE_MS so the timing feels deliberate */}
      <div className="h-0.5 bg-slate-100 dark:bg-slate-800 relative overflow-hidden">
        <div
          key={idx + (paused ? "-paused" : "-running")}
          className={`absolute inset-y-0 left-0 bg-teal-500 ${paused || reduceMotion ? "" : "carousel-progress"}`}
          style={paused || reduceMotion ? { width: "0%" } : { width: "100%", animationDuration: `${SLIDE_MS}ms` }}
        />
      </div>

      {/* Header */}
      <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
          Live Operational Insights
        </span>
        <span className="text-[10px] text-teal-700 dark:text-teal-300 bg-teal-50 dark:bg-teal-900/40 border border-teal-100 dark:border-teal-800 px-2 py-0.5 rounded-full">
          Preview
        </span>
      </div>
      <p className="px-5 pt-4 pb-1 text-[12px] text-slate-500 dark:text-slate-400 leading-relaxed">
        Observations surface in real time as you answer guided questions.
      </p>

      {/* The single visible slide */}
      <div className="p-5">
        <div
          key={idx}
          data-testid={`insight-slide-${idx}`}
          className="relative bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800 overflow-hidden carousel-slide-in"
        >
          <div className={`absolute left-0 top-0 h-full w-1 ${SEV_BAR[current.sev]}`} />
          <div className="p-3.5 pl-4 min-h-[112px]">
            <p className="text-[10px] uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400">
              {current.type}
            </p>
            <p className="mt-1 text-[13.5px] font-semibold text-slate-900 dark:text-slate-50 leading-snug">
              {current.title}
            </p>
            <p className="mt-1 text-[12.5px] text-slate-600 dark:text-slate-300 leading-relaxed">
              {current.body}
            </p>
          </div>
        </div>

        {/* Dots */}
        <div className="mt-4 flex items-center justify-center gap-1.5" role="tablist">
          {INSIGHTS.map((_, i) => (
            <button
              key={i}
              role="tab"
              aria-selected={i === idx}
              aria-label={`Insight ${i + 1} of ${INSIGHTS.length}`}
              data-testid={`insight-dot-${i}`}
              onClick={() => setIdx(i)}
              className={`h-1.5 rounded-full transition-all ${
                i === idx
                  ? "w-6 bg-slate-900 dark:bg-slate-100"
                  : "w-1.5 bg-slate-300 dark:bg-slate-700 hover:bg-slate-400"
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

